// src/components/dashboard/student/StudentTransactionsPage.tsx

import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  CreditCard,
  ArrowLeft,
  Search,
  Filter,
  ChevronDown,
  ChevronUp,
  Loader,
  Receipt,
  CheckCircle,
  XCircle,
  Clock,
  AlertCircle,
  Download,
  ExternalLink,
  Calendar,
  TrendingUp,
  TrendingDown,
  Wallet,
  BookOpen,
  Users,
  Award,
  Menu,
  Sparkles,
} from "lucide-react";
import { paymentApi, showError, showSuccess } from "../../../lib";
import { useAuth } from "../../../context/AuthContext";

interface Transaction {
  _id: string;
  user: {
    _id: string;
    name: string;
    email: string;
    avatar?: string;
  };
  course?: {
    _id: string;
    title: string;
    thumbnail?: string;
  };
  amount: number;
  currency: string;
  paymentMethod: string;
  transactionId: string;
  reference: string;
  status: 'pending' | 'completed' | 'failed' | 'refunded';
  type: 'course_enrollment' | 'mentorship_session' | 'certificate' | 'subscription';
  description: string;
  metadata?: any;
  createdAt: string;
  updatedAt: string;
}

const StudentTransactionsPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("");
  const [typeFilter, setTypeFilter] = useState<string>("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [sortBy, setSortBy] = useState<"date-desc" | "date-asc" | "amount-desc" | "amount-asc">("date-desc");
  const [showSortMenu, setShowSortMenu] = useState(false);
  const [showStatusFilter, setShowStatusFilter] = useState(false);
  const [showTypeFilter, setShowTypeFilter] = useState(false);
  const [expandedTransaction, setExpandedTransaction] = useState<string | null>(null);
  const [selectedTransaction, setSelectedTransaction] = useState<Transaction | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const limit = 10;

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  useEffect(() => {
    fetchTransactions();
  }, [currentPage, statusFilter, typeFilter, sortBy]);

  const fetchTransactions = async () => {
    try {
      setLoading(true);
      const response = await paymentApi.getMyTransactions({
        page: currentPage,
        limit,
        status: statusFilter || undefined,
        type: typeFilter || undefined,
      });
      if (response.success) {
        setTransactions(response.data);
        setTotalPages(response.pagination.pages);
        setTotalItems(response.pagination.total);
      }
    } catch (err) {
      console.error("Failed to fetch transactions:", err);
      showError("Failed to load transactions");
    } finally {
      setLoading(false);
    }
  };

  const getStatusConfig = (status: string) => {
    const configs: Record<string, { bg: string; text: string; icon: React.ReactNode; label: string }> = {
      pending: {
        bg: "bg-amber-50 border-amber-200",
        text: "text-amber-700",
        icon: <Clock className="w-3.5 h-3.5" />,
        label: "Pending",
      },
      completed: {
        bg: "bg-emerald-50 border-emerald-200",
        text: "text-emerald-700",
        icon: <CheckCircle className="w-3.5 h-3.5" />,
        label: "Completed",
      },
      failed: {
        bg: "bg-red-50 border-red-200",
        text: "text-red-700",
        icon: <XCircle className="w-3.5 h-3.5" />,
        label: "Failed",
      },
      refunded: {
        bg: "bg-purple-50 border-purple-200",
        text: "text-purple-700",
        icon: <AlertCircle className="w-3.5 h-3.5" />,
        label: "Refunded",
      },
    };
    return configs[status] || configs.pending;
  };

  const getTypeConfig = (type: string) => {
    const configs: Record<string, { icon: React.ReactNode; label: string }> = {
      course_enrollment: {
        icon: <BookOpen className="w-4 h-4 text-blue-500" />,
        label: "Course",
      },
      mentorship_session: {
        icon: <Users className="w-4 h-4 text-purple-500" />,
        label: "Mentorship",
      },
      certificate: {
        icon: <Award className="w-4 h-4 text-amber-500" />,
        label: "Certificate",
      },
      subscription: {
        icon: <Wallet className="w-4 h-4 text-emerald-500" />,
        label: "Subscription",
      },
    };
    return configs[type] || configs.course_enrollment;
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case "course_enrollment":
        return <BookOpen className="w-5 h-5 text-blue-500" />;
      case "mentorship_session":
        return <Users className="w-5 h-5 text-purple-500" />;
      case "certificate":
        return <Award className="w-5 h-5 text-amber-500" />;
      case "subscription":
        return <Wallet className="w-5 h-5 text-emerald-500" />;
      default:
        return <Receipt className="w-5 h-5 text-gray-500" />;
    }
  };

  const formatCurrency = (amount: number, currency: string = "XAF") => {
    return new Intl.NumberFormat("fr-CM", {
      style: "currency",
      currency: currency,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return {
      date: date.toLocaleDateString("en-US", {
        weekday: "short",
        month: "short",
        day: "numeric",
      }),
      time: date.toLocaleTimeString("en-US", {
        hour: "numeric",
        minute: "2-digit",
      }),
      full: date.toLocaleString("en-US", {
        weekday: "short",
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
      }),
    };
  };

  const filteredTransactions = useMemo(() => {
    let result = [...transactions];
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(t =>
        t.description?.toLowerCase().includes(query) ||
        t.transactionId?.toLowerCase().includes(query) ||
        t.reference?.toLowerCase().includes(query) ||
        t.course?.title?.toLowerCase().includes(query)
      );
    }
    return result;
  }, [transactions, searchQuery]);

  const getStatusBadge = (status: string) => {
    const config = getStatusConfig(status);
    return (
      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${config.bg} ${config.text}`}>
        {config.icon}
        {config.label}
      </span>
    );
  };

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const clearFilters = () => {
    setStatusFilter("");
    setTypeFilter("");
    setSearchQuery("");
    setCurrentPage(1);
  };

  const activeFilterCount = (statusFilter ? 1 : 0) + (typeFilter ? 1 : 0) + (searchQuery ? 1 : 0);

  // Loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px] bg-gradient-to-br from-orange-50 via-white to-orange-50/30">
        <div className="text-center">
          <Loader className="w-8 h-8 mx-auto mb-3 text-orange-500 animate-spin" />
          <p className="text-xs text-gray-500">Loading transactions...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-4 bg-gradient-to-br from-orange-50 via-white to-orange-50/30 md:py-6">
      <div className="px-4 mx-auto max-w-7xl md:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-5">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate(-1)}
                className="p-2 text-gray-500 transition-colors rounded-lg hover:bg-gray-100"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div className="flex items-center gap-2">
                <div className="flex items-center justify-center w-10 h-10 rounded-lg shadow-sm bg-gradient-to-r from-emerald-600 to-emerald-500">
                  <Receipt className="w-5 h-5 text-white" />
                </div>
                <h1 className="text-lg font-bold text-gray-900 md:text-xl">
                  Transactions
                </h1>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm font-semibold text-gray-700">
                {totalItems}
              </span>
              <span className="text-xs text-gray-400">total</span>
            </div>
          </div>
          <p className="text-xs text-gray-500 ml-14">
            View all your payment history and transactions
          </p>
        </div>

        {/* Stats Summary */}
        <div className="grid grid-cols-2 gap-3 mb-5 sm:grid-cols-4">
          <div className="p-3 bg-white border border-gray-100 shadow-sm rounded-xl">
            <p className="text-[10px] text-gray-500">Total Spent</p>
            <p className="text-sm font-bold text-gray-900">
              {formatCurrency(
                transactions
                  .filter(t => t.status === "completed")
                  .reduce((sum, t) => sum + t.amount, 0)
              )}
            </p>
          </div>
          <div className="p-3 bg-white border border-gray-100 shadow-sm rounded-xl">
            <p className="text-[10px] text-gray-500">Completed</p>
            <p className="text-sm font-bold text-emerald-600">
              {transactions.filter(t => t.status === "completed").length}
            </p>
          </div>
          <div className="p-3 bg-white border border-gray-100 shadow-sm rounded-xl">
            <p className="text-[10px] text-gray-500">Pending</p>
            <p className="text-sm font-bold text-amber-600">
              {transactions.filter(t => t.status === "pending").length}
            </p>
          </div>
          <div className="p-3 bg-white border border-gray-100 shadow-sm rounded-xl">
            <p className="text-[10px] text-gray-500">Failed</p>
            <p className="text-sm font-bold text-red-600">
              {transactions.filter(t => t.status === "failed").length}
            </p>
          </div>
        </div>

        {/* Filters */}
        <div className="mb-5">
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative flex-1 min-w-[140px]">
              <Search className="absolute w-4 h-4 text-gray-400 -translate-y-1/2 left-3 top-1/2" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search transactions..."
                className="w-full py-2 pr-4 text-sm bg-white border border-gray-200 rounded-lg pl-9 focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500"
              />
            </div>

            {/* Status Filter */}
            <div className="relative">
              <button
                onClick={() => setShowStatusFilter(!showStatusFilter)}
                className={`flex items-center gap-1.5 px-3 py-2 text-sm border rounded-lg transition-all ${
                  statusFilter
                    ? "bg-orange-50 border-orange-200 text-orange-600"
                    : "bg-white border-gray-200 text-gray-600 hover:border-gray-300"
                }`}
              >
                <Filter className="w-4 h-4" />
                <span>Status</span>
                {statusFilter && (
                  <span className="flex items-center justify-center w-4 h-4 text-xs text-white bg-orange-500 rounded-full">
                    •
                  </span>
                )}
                <ChevronDown className={`w-3.5 h-3.5 transition-transform ${showStatusFilter ? "rotate-180" : ""}`} />
              </button>
              {showStatusFilter && (
                <div className="absolute left-0 z-10 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg min-w-[150px] overflow-hidden">
                  {["", "pending", "completed", "failed", "refunded"].map((s) => (
                    <button
                      key={s || "all"}
                      onClick={() => {
                        setStatusFilter(s);
                        setShowStatusFilter(false);
                        setCurrentPage(1);
                      }}
                      className={`w-full px-3 py-2 text-left text-sm hover:bg-orange-50 transition-colors ${
                        statusFilter === s ? "bg-orange-50 text-orange-600 font-medium" : "text-gray-700"
                      }`}
                    >
                      {s ? s.charAt(0).toUpperCase() + s.slice(1) : "All Status"}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Type Filter */}
            <div className="relative">
              <button
                onClick={() => setShowTypeFilter(!showTypeFilter)}
                className={`flex items-center gap-1.5 px-3 py-2 text-sm border rounded-lg transition-all ${
                  typeFilter
                    ? "bg-purple-50 border-purple-200 text-purple-600"
                    : "bg-white border-gray-200 text-gray-600 hover:border-gray-300"
                }`}
              >
                <BookOpen className="w-4 h-4" />
                <span>Type</span>
                {typeFilter && (
                  <span className="flex items-center justify-center w-4 h-4 text-xs text-white bg-purple-500 rounded-full">
                    •
                  </span>
                )}
                <ChevronDown className={`w-3.5 h-3.5 transition-transform ${showTypeFilter ? "rotate-180" : ""}`} />
              </button>
              {showTypeFilter && (
                <div className="absolute left-0 z-10 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg min-w-[150px] overflow-hidden">
                  {["", "course_enrollment", "mentorship_session", "certificate", "subscription"].map((t) => (
                    <button
                      key={t || "all"}
                      onClick={() => {
                        setTypeFilter(t);
                        setShowTypeFilter(false);
                        setCurrentPage(1);
                      }}
                      className={`w-full px-3 py-2 text-left text-sm hover:bg-purple-50 transition-colors ${
                        typeFilter === t ? "bg-purple-50 text-purple-600 font-medium" : "text-gray-700"
                      }`}
                    >
                      {t ? t.replace("_", " ").charAt(0).toUpperCase() + t.slice(1).replace("_", " ") : "All Types"}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Sort */}
            <div className="relative ml-auto">
              <button
                onClick={() => setShowSortMenu(!showSortMenu)}
                className="flex items-center gap-1.5 px-3 py-2 text-sm bg-white border border-gray-200 rounded-lg hover:border-gray-300 transition-all"
              >
                <TrendingUp className="w-4 h-4 text-gray-400" />
                <span className="hidden sm:inline">
                  {sortBy === "date-desc" ? "Newest" :
                   sortBy === "date-asc" ? "Oldest" :
                   sortBy === "amount-desc" ? "Highest" : "Lowest"}
                </span>
                <ChevronDown className={`w-3.5 h-3.5 transition-transform ${showSortMenu ? "rotate-180" : ""}`} />
              </button>
              {showSortMenu && (
                <div className="absolute right-0 z-10 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg min-w-[150px] overflow-hidden">
                  {[
                    { value: "date-desc", label: "Newest First" },
                    { value: "date-asc", label: "Oldest First" },
                    { value: "amount-desc", label: "Highest Amount" },
                    { value: "amount-asc", label: "Lowest Amount" },
                  ].map((s) => (
                    <button
                      key={s.value}
                      onClick={() => {
                        setSortBy(s.value as typeof sortBy);
                        setShowSortMenu(false);
                        setCurrentPage(1);
                      }}
                      className={`w-full px-3 py-2 text-left text-sm hover:bg-orange-50 transition-colors ${
                        sortBy === s.value ? "bg-orange-50 text-orange-600 font-medium" : "text-gray-700"
                      }`}
                    >
                      {s.label}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Clear Filters */}
            {activeFilterCount > 0 && (
              <button
                onClick={clearFilters}
                className="px-3 py-2 text-xs font-medium text-gray-500 transition-all bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                Clear ({activeFilterCount})
              </button>
            )}
          </div>
        </div>

        {/* Transactions List */}
        {filteredTransactions.length === 0 ? (
          <div className="py-12 text-center bg-white border border-gray-100 rounded-xl">
            <div className="flex items-center justify-center w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full">
              <Receipt className="w-8 h-8 text-gray-400" />
            </div>
            <h3 className="mb-2 text-sm font-medium text-gray-900">No transactions found</h3>
            <p className="text-xs text-gray-500">
              {searchQuery || statusFilter || typeFilter
                ? "Try adjusting your filters"
                : "Your transactions will appear here"}
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {filteredTransactions.map((transaction) => {
              const statusConfig = getStatusConfig(transaction.status);
              const typeConfig = getTypeConfig(transaction.type);
              const formattedDate = formatDate(transaction.createdAt);
              const isExpanded = expandedTransaction === transaction._id;

              return (
                <div
                  key={transaction._id}
                  className="overflow-hidden transition-all bg-white border border-gray-100 shadow-sm rounded-xl hover:shadow-md"
                >
                  <div className="p-4">
                    <div className="flex flex-wrap items-center gap-3">
                      {/* Icon */}
                      <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-gray-50 shrink-0">
                        {getTypeIcon(transaction.type)}
                      </div>

                      {/* Info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <h4 className="text-sm font-semibold text-gray-900 truncate">
                            {transaction.description || `${typeConfig.label} Payment`}
                          </h4>
                          {getStatusBadge(transaction.status)}
                        </div>
                        <div className="flex flex-wrap items-center mt-1 gap-x-3 gap-y-1">
                          <span className="text-xs text-gray-500">
                            {formattedDate.date} at {formattedDate.time}
                          </span>
                          <span className="text-xs text-gray-400">•</span>
                          <span className="text-xs text-gray-500">
                            {transaction.transactionId?.slice(0, 12) || "N/A"}
                          </span>
                          {transaction.course && (
                            <>
                              <span className="text-xs text-gray-400">•</span>
                              <span className="text-xs text-gray-500 truncate max-w-[120px]">
                                {transaction.course.title}
                              </span>
                            </>
                          )}
                        </div>
                      </div>

                      {/* Amount */}
                      <div className="flex items-center gap-3">
                        <div className="text-right">
                          <p className={`text-sm font-bold ${transaction.status === "completed" ? "text-emerald-600" : transaction.status === "failed" ? "text-red-600" : "text-gray-600"}`}>
                            {formatCurrency(transaction.amount, transaction.currency)}
                          </p>
                          <p className="text-[10px] text-gray-400 uppercase">
                            {transaction.paymentMethod || "Unknown"}
                          </p>
                        </div>
                        <button
                          onClick={() => {
                            setSelectedTransaction(transaction);
                            setShowDetailModal(true);
                          }}
                          className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                        >
                          <ChevronDown className={`w-4 h-4 transition-transform ${isExpanded ? "rotate-180" : ""}`} />
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Expanded Details */}
                  {isExpanded && (
                    <div className="px-4 pb-4 border-t border-gray-100 bg-gray-50">
                      <div className="grid grid-cols-2 gap-3 pt-3 sm:grid-cols-4">
                        <div>
                          <p className="text-[10px] text-gray-400">Transaction ID</p>
                          <p className="text-xs font-medium text-gray-700 truncate">
                            {transaction.transactionId || "N/A"}
                          </p>
                        </div>
                        <div>
                          <p className="text-[10px] text-gray-400">Reference</p>
                          <p className="text-xs font-medium text-gray-700 truncate">
                            {transaction.reference || "N/A"}
                          </p>
                        </div>
                        <div>
                          <p className="text-[10px] text-gray-400">Payment Method</p>
                          <p className="text-xs font-medium text-gray-700 capitalize">
                            {transaction.paymentMethod || "Unknown"}
                          </p>
                        </div>
                        <div>
                          <p className="text-[10px] text-gray-400">Date</p>
                          <p className="text-xs font-medium text-gray-700">
                            {formattedDate.full}
                          </p>
                        </div>
                      </div>
                      {transaction.metadata && (
                        <div className="pt-2 mt-2 border-t border-gray-200">
                          <p className="text-[10px] text-gray-400 mb-1">Additional Details</p>
                          <pre className="p-2 overflow-x-auto text-xs text-gray-600 bg-white rounded-lg">
                            {JSON.stringify(transaction.metadata, null, 2)}
                          </pre>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex flex-col items-center gap-3 mt-6">
            <div className="flex items-center gap-2">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className={`p-2 rounded-lg border transition-colors ${
                  currentPage === 1
                    ? "bg-gray-100 text-gray-400 cursor-not-allowed border-gray-200"
                    : "bg-white text-gray-600 hover:bg-gray-50 border-gray-200"
                }`}
              >
                <ArrowLeft className="w-4 h-4" />
              </button>
              <div className="flex items-center gap-1">
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (currentPage <= 3) {
                    pageNum = i + 1;
                  } else if (currentPage >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = currentPage - 2 + i;
                  }
                  return (
                    <button
                      key={pageNum}
                      onClick={() => handlePageChange(pageNum)}
                      className={`w-8 h-8 rounded-lg text-sm font-medium transition-colors ${
                        currentPage === pageNum
                          ? "bg-orange-500 text-white"
                          : "bg-white text-gray-600 hover:bg-gray-50 border border-gray-200"
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
              </div>
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className={`p-2 rounded-lg border transition-colors ${
                  currentPage === totalPages
                    ? "bg-gray-100 text-gray-400 cursor-not-allowed border-gray-200"
                    : "bg-white text-gray-600 hover:bg-gray-50 border-gray-200"
                }`}
              >
                <ArrowLeft className="w-4 h-4 rotate-180" />
              </button>
            </div>
            <div className="text-xs text-gray-400">
              Showing {((currentPage - 1) * limit) + 1} - {Math.min(currentPage * limit, totalItems)} of {totalItems} transactions
            </div>
          </div>
        )}
      </div>

      {/* Transaction Detail Modal */}
      {showDetailModal && selectedTransaction && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
          onClick={(e) => e.target === e.currentTarget && setShowDetailModal(false)}
        >
          <div className="w-full max-w-md overflow-hidden bg-white shadow-2xl rounded-2xl animate-fade-in-up">
            <div className="px-5 py-4 bg-gradient-to-r from-emerald-600 to-emerald-500">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-bold text-white">Transaction Details</h2>
                <button
                  onClick={() => setShowDetailModal(false)}
                  className="p-1 text-white/80 hover:text-white"
                >
                  <XCircle className="w-5 h-5" />
                </button>
              </div>
            </div>
            <div className="p-5 space-y-4">
              <div className="pb-4 text-center border-b border-gray-100">
                <div className="flex items-center justify-center mb-2">
                  {getTypeIcon(selectedTransaction.type)}
                </div>
                <p className="text-xl font-bold text-gray-900">
                  {formatCurrency(selectedTransaction.amount, selectedTransaction.currency)}
                </p>
                <p className="text-sm text-gray-500">{selectedTransaction.description}</p>
                {getStatusBadge(selectedTransaction.status)}
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <p className="text-[10px] text-gray-400">Transaction ID</p>
                  <p className="text-xs font-medium text-gray-700 truncate">{selectedTransaction.transactionId || "N/A"}</p>
                </div>
                <div>
                  <p className="text-[10px] text-gray-400">Reference</p>
                  <p className="text-xs font-medium text-gray-700 truncate">{selectedTransaction.reference || "N/A"}</p>
                </div>
                <div>
                  <p className="text-[10px] text-gray-400">Payment Method</p>
                  <p className="text-xs font-medium text-gray-700 capitalize">{selectedTransaction.paymentMethod || "Unknown"}</p>
                </div>
                <div>
                  <p className="text-[10px] text-gray-400">Date</p>
                  <p className="text-xs font-medium text-gray-700">{formatDate(selectedTransaction.createdAt).full}</p>
                </div>
                {selectedTransaction.course && (
                  <div className="col-span-2">
                    <p className="text-[10px] text-gray-400">Course</p>
                    <p className="text-xs font-medium text-gray-700">{selectedTransaction.course.title}</p>
                  </div>
                )}
              </div>

              {selectedTransaction.metadata && (
                <div className="pt-2 border-t border-gray-100">
                  <p className="text-[10px] text-gray-400 mb-1">Additional Details</p>
                  <pre className="p-2 overflow-x-auto text-xs text-gray-600 rounded-lg bg-gray-50 max-h-32">
                    {JSON.stringify(selectedTransaction.metadata, null, 2)}
                  </pre>
                </div>
              )}
            </div>
            <div className="px-5 py-3 border-t border-gray-100 bg-gray-50">
              <button
                onClick={() => setShowDetailModal(false)}
                className="w-full py-2 text-sm font-medium text-center text-gray-600 transition-colors bg-white border border-gray-200 rounded-lg hover:bg-gray-50"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes fade-in-up {
          from { opacity: 0; transform: translateY(20px) scale(0.95); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
        .animate-fade-in-up {
          animation: fade-in-up 0.3s ease-out;
        }
      `}</style>
    </div>
  );
};

export default StudentTransactionsPage;